import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader, AlertCircle, CheckCircle, ClipboardList, Plus } from 'lucide-react';
import { moveProcessService } from '../../../../services/moveProcessService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
    MoveProcess, MoveProcessStatus, AddMoveProcessStepRequest
} from '../move-processes/moveProcessTypes';

/** One step to add, in backend-ready form. */
export interface PlanStepToAdd {
    fromBinCode: string;
    toBinCode: string;
    itemNo: string;
    quantity: number;
    batchNo?: string;
    serialNo?: string;
}

interface AddToPlanModalProps {
    isOpen: boolean;
    warehouseID: string;
    steps: PlanStepToAdd[];
    /** Short label describing the source context (e.g. "3 lokasjoner → 020305"). */
    contextLabel?: string;
    onClose: () => void;
    /** Called after steps were added successfully. Parent can show a toast etc. */
    onAdded: (planId: number, planName: string, addedCount: number) => void;
}

/**
 * Lets the user pick an existing Draft plan or create a new one, then adds
 * the provided steps. Used from all three heatmap modals (single, batch, suggest).
 */
export const AddToPlanModal: React.FC<AddToPlanModalProps> = ({
    isOpen, warehouseID, steps, contextLabel, onClose, onAdded
}) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<'existing' | 'new'>('new');
    const [existingPlans, setExistingPlans] = useState<MoveProcess[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [newPlanName, setNewPlanName] = useState('');
    const [newPlanDescription, setNewPlanDescription] = useState('');
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load Draft plans when the modal opens so the user can pick one.
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        (async () => {
            setLoadingPlans(true);
            setError(null);
            try {
                const plans = await moveProcessService.list(warehouseID, MoveProcessStatus.Draft);
                if (cancelled) return;
                setExistingPlans(plans);
                // Default to existing mode if any drafts exist, else new mode.
                if (plans.length > 0) {
                    setMode('existing');
                    setSelectedPlanId(plans[0].id);
                } else {
                    setMode('new');
                }
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.addToPlan.errors.loadPlansFailed', {}, 'Kunne ikke hente planer'));
            } finally {
                if (!cancelled) setLoadingPlans(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, warehouseID, t]);

    const totalQty = useMemo(() => steps.reduce((sum, s) => sum + s.quantity, 0), [steps]);

    const canConfirm = !busy && !loadingPlans && steps.length > 0 && (
        (mode === 'existing' && selectedPlanId !== null) ||
        (mode === 'new' && newPlanName.trim().length > 0)
    );

    const handleConfirm = async () => {
        setBusy(true);
        setError(null);
        try {
            let planId: number;
            let planName: string;

            if (mode === 'new') {
                const created = await moveProcessService.create({
                    warehouseID,
                    name: newPlanName.trim(),
                    description: newPlanDescription.trim() || undefined
                });
                planId = created.id;
                planName = created.name;
            } else {
                const chosen = existingPlans.find(p => p.id === selectedPlanId)!;
                planId = chosen.id;
                planName = chosen.name;
            }

            const stepsBody: AddMoveProcessStepRequest[] = steps.map(s => ({
                fromBinCode: s.fromBinCode,
                toBinCode: s.toBinCode,
                itemNo: s.itemNo,
                quantity: s.quantity,
                batchNo: s.batchNo || undefined,
                serialNo: s.serialNo || undefined
            }));

            const result = await moveProcessService.addStepsBatch(planId, { steps: stepsBody });

            if (!result.success || result.failedCount > 0) {
                if (result.successCount > 0) {
                    // Partial success — still inform parent but show warning
                    onAdded(planId, planName, result.successCount);
                } else {
                    setError(result.errorMessage || t('manager.modules.locationHeatmap.addToPlan.errors.addStepsFailed', {}, 'Steg kunne ikke legges til'));
                    return;
                }
            } else {
                onAdded(planId, planName, result.successCount);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.addToPlan.errors.addToPlanFailed', {}, 'Kunne ikke legge til i plan'));
        } finally {
            setBusy(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <ClipboardList className="w-5 h-5 text-purple-600" />
                        <h2 className="text-lg font-bold">{t('manager.modules.locationHeatmap.addToPlan.title', {}, 'Legg i flytteplan')}</h2>
                    </div>
                    <button onClick={onClose} disabled={busy} className="p-1 rounded hover:bg-gray-100 disabled:opacity-50">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <div className="px-6 py-4 space-y-4">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700">
                        <div className="font-semibold">{t('manager.modules.locationHeatmap.addToPlan.addedToPlan', {}, 'Legges til i plan:')}</div>
                        <div className="mt-1">
                            {t('manager.modules.locationHeatmap.addToPlan.stepsSummary', { '0': steps.length, '1': totalQty }, `${steps.length} steg · total ${totalQty} stk`)}
                        </div>
                        {contextLabel && (
                            <div className="mt-1 text-gray-500">{contextLabel}</div>
                        )}
                    </div>

                    {loadingPlans ? (
                        <div className="flex items-center justify-center py-4">
                            <Loader className="w-5 h-5 animate-spin text-purple-600" />
                            <span className="ml-2 text-sm text-gray-600">{t('manager.modules.locationHeatmap.addToPlan.loadingPlans', {}, 'Henter planer...')}</span>
                        </div>
                    ) : (
                        <>
                            <div className="flex space-x-2">
                                <button
                                    type="button"
                                    onClick={() => setMode('existing')}
                                    disabled={existingPlans.length === 0}
                                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-semibold transition ${
                                        mode === 'existing'
                                            ? 'bg-purple-600 text-white border-purple-600'
                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                                    }`}
                                >
                                    {t('manager.modules.locationHeatmap.addToPlan.existingPlan', {}, 'Eksisterende plan')}
                                    {existingPlans.length > 0 && (
                                        <span className="ml-1 text-xs opacity-80">({existingPlans.length})</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode('new')}
                                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-semibold transition ${
                                        mode === 'new'
                                            ? 'bg-purple-600 text-white border-purple-600'
                                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <Plus className="w-3.5 h-3.5 inline mr-1" />
                                    {t('manager.modules.locationHeatmap.addToPlan.newPlan', {}, 'Ny plan')}
                                </button>
                            </div>

                            {mode === 'existing' && existingPlans.length > 0 && (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.locationHeatmap.addToPlan.selectPlanDraft', {}, 'Velg plan (Kladd)')}</label>
                                    <select
                                        value={selectedPlanId ?? ''}
                                        onChange={e => setSelectedPlanId(parseInt(e.target.value, 10))}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    >
                                        {existingPlans.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {t('manager.modules.locationHeatmap.addToPlan.planOption', { '0': p.name, '1': p.stepCount }, `${p.name} (${p.stepCount} steg)`)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {mode === 'new' && (
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.locationHeatmap.addToPlan.planNameLabel', {}, 'Plan-navn *')}</label>
                                        <input
                                            type="text"
                                            value={newPlanName}
                                            onChange={e => setNewPlanName(e.target.value)}
                                            maxLength={100}
                                            placeholder={t('manager.modules.locationHeatmap.addToPlan.planNamePlaceholder', {}, "F.eks. 'Klargjør for containermottak 22. april'")}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                            autoFocus
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1">{t('manager.modules.locationHeatmap.addToPlan.descriptionLabel', {}, 'Beskrivelse (valgfri)')}</label>
                                        <textarea
                                            value={newPlanDescription}
                                            onChange={e => setNewPlanDescription(e.target.value)}
                                            rows={2}
                                            maxLength={500}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-800 flex items-start space-x-2">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="flex justify-end space-x-2 px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold disabled:opacity-50"
                    >
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center space-x-2"
                    >
                        {busy ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                <span>{t('manager.modules.locationHeatmap.addToPlan.adding', {}, 'Legger til...')}</span>
                            </>
                        ) : (
                            <>
                                <CheckCircle className="w-4 h-4" />
                                <span>{t('manager.modules.locationHeatmap.actions.addToPlan', {}, 'Legg i plan')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
